import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Popover } from "@vd/ui";
import { CalendarDays, Download, Settings2 } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { type CostQuery, FEATURE_LABEL, type Feature, SOURCE_LABEL, type Source } from "../../api/costs";
import { costsExportPath, costsQuery } from "../../api/ops";
import { CompanySelect, useCompanyChoice } from "../../components/CompanyPicker";
import { CostReportView } from "../../components/costs/CostReportView";
import { CostSettings } from "../../components/costs/CostSettings";
import { SpendAlertBanner } from "../../components/costs/SpendAlertBanner";
import { Button, ErrorState, Field, Input, Loading, PageHeader, Segmented } from "../../components/ui";
import { API_BASE } from "../../config";
import { type Preset, presetOf, presetRange } from "../../lib/costs";
import { fmtDate, todayIso } from "../../lib/format";
import { useNavigate } from "../../lib/navigate";
import type { SpendBy } from "../../lib/spend";

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const searchSchema = z.object({
  company: z.string().optional().catch(undefined),
  from: day.optional().catch(undefined),
  to: day.optional().catch(undefined),
  group: z.enum(["day", "week", "month"]).optional().catch(undefined),
  tab: z.enum(["features", "reps", "campaigns", "providers", "routes", "fixed"]).optional().catch(undefined),
  by: z.enum(["source", "feature", "rep"]).optional().catch(undefined),
  source: z.string().optional().catch(undefined),
  feature: z.string().optional().catch(undefined),
  user_id: z.string().optional().catch(undefined),
  settings: z.enum(["budgets", "alerts", "fixed", "prices"]).optional().catch(undefined),
});
type Search = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/costs")({
  staticData: { crumb: { label: "Costs", parents: [] } },
  validateSearch: (s) => searchSchema.parse(s),
  component: CostsPage,
});

function CostsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<Search>) => navigate({ search: (p) => ({ ...p, ...patch }), replace: true });
  const companies = useCompanyChoice(search.company, (company) => set({ company }));
  const today = todayIso();
  const def = presetRange("this_month", today);
  const q: CostQuery = {
    from: search.from ?? def.from,
    to: search.to ?? def.to,
    group: search.group ?? "day",
    source: search.source as CostQuery["source"],
    feature: search.feature as CostQuery["feature"],
    user_id: search.user_id,
  };
  const by: SpendBy = search.by ?? "source";
  const report = useQuery({ ...costsQuery(search.company ?? "", q), enabled: !!search.company });
  const [customOpen, setCustomOpen] = useState(false);
  const repName = (id: string): string | null =>
    report.data?.reps.find((r) => r.user_id === id)?.name ?? null;
  const filterLabel = search.source
    ? (SOURCE_LABEL[search.source as Source] ?? search.source)
    : search.feature
      ? (FEATURE_LABEL[search.feature as Feature] ?? search.feature)
      : search.user_id
        ? (repName(search.user_id) ?? "One rep")
        : null;
  const preset = presetOf(q.from, q.to, today);

  return (
    <>
      <PageHeader
        title="Costs"
        actions={
          <>
            <CompanySelect
              value={search.company}
              companies={companies.data?.items ?? []}
              onChange={(company) => set({ company })}
            />
            {search.company ? (
              <a
                className="press inline-flex h-8 items-center gap-1.5 rounded-pill bg-surface-3 px-3 font-medium text-ink text-ui shadow-control"
                href={`${API_BASE}${costsExportPath(search.company, q)}`}
                download
              >
                <Download className="size-4" aria-hidden />
                Export CSV
              </a>
            ) : null}
            {search.company ? (
              <Button onClick={() => set({ settings: "budgets" })}>
                <Settings2 className="size-4" aria-hidden />
                Settings
              </Button>
            ) : null}
          </>
        }
      >
        {`${fmtDate(q.from)} to ${fmtDate(q.to)}, UTC days`}
      </PageHeader>
      {search.company ? <SpendAlertBanner company={search.company} /> : null}
      {filterLabel ? (
        <div className="mb-3 flex items-center gap-2 text-ui">
          <span className="rounded-pill bg-accent-soft px-3 py-1 text-accent-ink">Only {filterLabel}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => set({ source: undefined, feature: undefined, user_id: undefined })}
          >
            Show everything
          </Button>
        </div>
      ) : null}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Segmented
          label="Range"
          value={preset ?? ("custom" as Preset | "custom")}
          onChange={(v) => (v === "custom" ? setCustomOpen(true) : set(presetRange(v as Preset, today)))}
          options={[
            { value: "this_month", label: "This month" },
            { value: "last_month", label: "Last month" },
            { value: "last_90", label: "Last 90 days" },
            { value: "custom", label: "Custom" },
          ]}
        />
        <Popover
          open={customOpen}
          onOpenChange={setCustomOpen}
          className="w-[300px] p-3"
          trigger={
            <button
              type="button"
              className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-ink-2 text-ui hover:bg-fill-2 hover:text-ink"
              aria-label={`Dates: ${fmtDate(q.from)} to ${fmtDate(q.to)}. Change`}
            >
              <CalendarDays className="size-4" aria-hidden />
              {`${fmtDate(q.from)} to ${fmtDate(q.to)}`}
            </button>
          }
        >
          <div className="flex flex-col gap-3">
            <Field label="From">
              <Input
                type="date"
                aria-label="From date"
                value={q.from}
                max={q.to}
                onChange={(e) => e.target.value && set({ from: e.target.value })}
              />
            </Field>
            <Field label="To">
              <Input
                type="date"
                aria-label="To date"
                value={q.to}
                min={q.from}
                onChange={(e) => e.target.value && set({ to: e.target.value })}
              />
            </Field>
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setCustomOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        </Popover>
        <span className="ml-auto">
          <Segmented
            label="Group by"
            value={q.group}
            onChange={(v) => set({ group: v === "day" ? undefined : v })}
            options={[
              { value: "day", label: "Day" },
              { value: "week", label: "Week" },
              { value: "month", label: "Month" },
            ]}
          />
        </span>
      </div>
      {companies.isError ? (
        <ErrorState error={companies.error} onRetry={() => companies.refetch()} />
      ) : !search.company || report.isPending ? (
        <Loading label="Adding up costs" />
      ) : report.isError ? (
        <ErrorState error={report.error} onRetry={() => report.refetch()} />
      ) : (
        <CostReportView
          r={report.data}
          tab={search.tab ?? "features"}
          onTab={(t) => set({ tab: t === "features" ? undefined : t })}
          by={by}
          filtered={!!filterLabel}
          onBy={(b) => set({ by: b === "source" ? undefined : b })}
          selected={by === "source" ? search.source : by === "feature" ? search.feature : search.user_id}
          onSelect={(kind, key) => {
            const clear = { source: undefined, feature: undefined, user_id: undefined };
            const same =
              (kind === "source" && search.source === key) ||
              (kind === "feature" && search.feature === key) ||
              (kind === "rep" && search.user_id === key);
            if (same || key === "none") return set(clear);
            set({
              ...clear,
              ...(kind === "source"
                ? { source: key }
                : kind === "feature"
                  ? { feature: key }
                  : { user_id: key }),
              tab: kind === "rep" ? "features" : "providers",
            });
          }}
        />
      )}
      {search.company ? (
        <CostSettings
          company={search.company}
          companyName={companies.data?.items.find((c) => c.id === search.company)?.name ?? ""}
          open={!!search.settings}
          pane={search.settings ?? "budgets"}
          onPane={(p) => set({ settings: p })}
          onClose={() => set({ settings: undefined })}
        />
      ) : null}
    </>
  );
}
